import React, { useState, useEffect } from "react";
import { Avatar, Box, Stack, Typography, IconButton, CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import { Images } from "../../assets/common";
import { useDispatch } from "react-redux";
import { addSongAction } from "../../action/songs";
import TipPopup from "../TipPopup";
import { useTheme, useMediaQuery } from "@mui/material";
import "../../pages/Home/Home.scss";


const FeaturedSection = () => {
  const [displayData, setDisplayData] = useState([]);
  const [visibleItems, setVisibleItems] = useState(5);
  const [loading, setLoading] = useState(false);
  const [openTipPopup, setOpenTipPopup] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:1150px)");

  const fetchMusicData = async () => {
    setLoading(true);
    try {
      const response = await ApiService.MUSIC_MOST_STREAM_WEEK();
      const enrichedData = await Promise.all(
        response.result.map(async (musicItem) => {
          const tipperResponse = await ApiService.INDIVIDUAL_SONG_TIPPER(musicItem._id);
          const topTipper = tipperResponse.result?.find((tipper) => tipper.musicId === musicItem._id) || null;
          return {
            ...musicItem,
            topTipperName: topTipper?.topTipperName || null,
            topTipperProfile: topTipper?.topTipperProfile?.filePath || Images.dummy,
          };
        })
      );
      setDisplayData(enrichedData);
    } catch (error) {
      console.error("Error fetching music data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMusicData();
  }, []);

  const handlePlaySong = (song) => {
    dispatch(addSongAction(song));
  };

  const handleTipClick = (song) => {
    setSelectedSong(song);
    setOpenTipPopup(true);
  };

  const handleTipPopupClose = () => {
    setOpenTipPopup(false);
    setSelectedSong(null);
  };

  const handleArtistClick = (artistId) => {
    history.push(`/app/ArtistProfile/${artistId}`);
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <section className="siki-featured-section siki-fade-up">
      <div className="siki-section-label">Top Charts</div>
      <div className="siki-section-header">
        <h2 className="siki-section-title">Most Streamed This Week</h2>
      </div>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress size={30} color="inherit" />
          </Box>
        ) : displayData.length > 0 ? (
          displayData.slice(0, visibleItems).map((musicItem, index) =>
            !isMobile ? (
            <Box
              key={musicItem?._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "12px 16px",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  "& .trending-number": {
                    color: "var(--siki-cyan)"
                  }
                },
              }}
              onClick={() => handlePlaySong(musicItem)}
            >
              <Typography
                className="trending-number"
                sx={{
                  minWidth: "24px",
                  fontFamily: "var(--siki-font-mono)",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "var(--siki-text-dim)",
                  transition: "color 0.2s ease"
                }}
              >
                {index + 1}
              </Typography>

              <Avatar
                src={musicItem?.thumbnailImg?.filePath || Images.dummy}
                alt={musicItem.title}
                sx={{ width: 48, height: 48, borderRadius: "6px" }}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "var(--siki-text)",
                    fontWeight: "600",
                    fontSize: "15px",
                    fontFamily: "var(--font-body)",
                    mb: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {musicItem.title}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    display: "inline-block",
                    color: "var(--siki-text-muted)",
                    fontSize: "13px",
                    fontFamily: "var(--font-body)",
                    cursor: "pointer",
                    "&:hover": { color: "var(--siki-cyan)" }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArtistClick(musicItem.uploader);
                  }}
                >
                  {musicItem.artistData?.[0]?.fullName || "Unknown Artist"}
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" gap={3} sx={{ ml: "auto" }}>
                <Box sx={{ textAlign: "center", minWidth: "60px" }}>
                  <Typography sx={{ fontSize: "11px", color: "var(--siki-text-dim)", mb: 0.5, fontFamily: "var(--siki-font-mono)" }}>
                    STREAMS
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "var(--siki-text)", fontWeight: "600" }}>
                    {Math.round(musicItem.countValue || musicItem.listens || musicItem.streamCount || 0).toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center", minWidth: "50px" }}>
                  <Typography sx={{ fontSize: "11px", color: "var(--siki-text-dim)", mb: 0.5, fontFamily: "var(--siki-font-mono)" }}>
                    TIME
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "var(--siki-text)", fontFamily: "var(--siki-font-mono)" }}>
                    {formatDuration(musicItem.duration)}
                  </Typography>
                </Box>

                {musicItem.topTipperName ? (
                  <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: "100px" }}>
                    <Avatar
                      src={musicItem?.topTipperProfile || Images.dummy}
                      alt={musicItem.topTipperName}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Typography sx={{ fontSize: "13px", color: "var(--siki-text)" }}>
                      {musicItem.topTipperName}
                    </Typography>
                  </Stack>
                ) : (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTipClick(musicItem);
                    }}
                    sx={{ minWidth: "100px" }}
                  >
                    <img src={Images.tip_player} alt="tip" style={{ width: "20px", height: "20px" }} />
                  </IconButton>
                )}
              </Stack>
            </Box>
            ) : (
              <Box
                key={musicItem?._id}
                sx={{
                  backgroundColor: (theme) => theme.palette.background.default,
                  borderRadius: "12px",
                  p: 2,
                  mb: 2,
                  transition: "0.2s",
                  "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ cursor: "pointer" }}
                  onClick={() => handlePlaySong(musicItem)}
                >
                  <Avatar
                    src={musicItem?.thumbnailImg?.filePath || Images.dummy}
                    alt={musicItem.title}
                    sx={{ width: 48, height: 48 }}
                  />

                  <Box>
                    <Typography fontWeight="600">
                      {musicItem.title}
                    </Typography>

                    <Typography
                      sx={{ color: theme.palette.warning.main, fontSize: "0.85rem" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArtistClick(musicItem.uploader);
                      }}
                    >
                      {musicItem.artistData?.[0]?.fullName || "Unknown Artist"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mt={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography fontSize="0.85rem" sx={{ color: (theme) => theme.palette.text.secondary }}>
                      {Math.round(musicItem.countValue || musicItem.listens || musicItem.streamCount || 0)} streams
                    </Typography>

                    <Typography fontSize="0.85rem" sx={{ color: (theme) => theme.palette.text.secondary }}>
                      {formatDuration(musicItem.duration)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {musicItem.topTipperName ? (
                      <>
                        <Avatar
                          src={musicItem?.topTipperProfile || Images.dummy}
                          sx={{ width: 28, height: 28 }}
                        />
                        <Typography fontSize="0.85rem">
                          {musicItem.topTipperName}
                        </Typography>
                      </>
                    ) : (
                      <IconButton onClick={() => handleTipClick(musicItem)}>
                        <img src={Images.tip_player} alt="tip" width={20} />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </Box>
            )
          )
        ) : (
          <Typography sx={{ textAlign: "center", color: (theme) => theme.palette.text.primary }}>No data available</Typography>
        )}
      </Box>

      {selectedSong && (
        <TipPopup
          artist={selectedSong.artistData?.[0]?.fullName || "Unknown Artist"}
          id={selectedSong._id}
          open={openTipPopup}
          isArtist={false}
          handleClose={handleTipPopupClose}
        />
      )}
    </section>
  );
};

export default FeaturedSection;
